import {
  Box,
  TextField,
  Select,
  MenuItem,
  Typography,
  Pagination,
  List,
  ListItem,
  InputLabel,
  FormControl,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import PropTypes from "prop-types";
import TabList from "./TabList.jsx";

export default function DataList({
  columns,
  data,
  isLoading = false,
  pageIndex = 0,
  pageSize = 10,
  totalRows,
  onChangePage,
  // onChangeSorting,
  onChangeFilters,
  onChangeGlobalFilter,
  toolbarActions = [],
  filterTabs,
  activeFilterTab,
  filterOptions = {},
  title,
  onRowClick,
  // storageKey,
  ...rest
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(pageIndex + 1);

  const pageCount = Math.ceil(totalRows / pageSize);

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", mt: 4 }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
      )}

      {/* Toolbar Actions */}
      <Box sx={{ display: "flex", gap: 2, m: 2, mt: 0 }}>
        {toolbarActions.map((action, idx) => (
          <Tooltip key={idx} title={action.label}>
            <span>
              <IconButton
                onClick={action.onClick}
                disabled={action.disabled}
                color={action.variant === "primary" ? "primary" : "default"}
              >
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </Box>

      {/* Suche */}
      <Box
        sx={{
          m: 2,
          mt: 0,
        }}
      >
        <TextField
          label="Suche"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
            onChangeGlobalFilter?.(e.target.value);
          }}
        />
      </Box>

      {/* Left side - Filter tabs */}
      {filterTabs && filterTabs.length > 0 && (
        <TabList
          tabs={filterTabs}
          activeTab={activeFilterTab}
          onTabChange={(tabKey) => {
            const tab = filterTabs.find((t) => t.key === tabKey);
            if (tab?.onClick) {
              tab.onClick(tabKey);
            }
          }}
          sx={{
            marginRight: "auto",
          }}
        />
      )}

      {/* Filter */}
      <Box sx={{ display: "flex", gap: 2, m: 2 }}>
        {Object.entries(filterOptions).map(([key, options]) => (
          <FormControl key={key} variant="outlined" sx={{ minWidth: 120 }}>
            <InputLabel>{key}</InputLabel>
            <Select
              label={key}
              value={filters[key] || ""}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  [key]: e.target.value,
                }));
                setPage(1);
                onChangeFilters?.({ ...filters, [key]: e.target.value });
              }}
            >
              <MenuItem value="">Alle</MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
      </Box>

      {/* Loader */}
      {isLoading ? (
        <SkeletonList />
      ) : (
        <List>
          {data.map((row, idx) => (
            <ListItem
              key={row.id || idx}
              sx={{
                borderBottom: "1px solid #eee",
                alignItems: "flex-start",
                flexDirection: "column",
                py: 2,
                cursor: onRowClick ? "pointer" : "default",
                "&:hover": {
                  backgroundColor: onRowClick ? "#f9f9f9" : "transparent",
                },
              }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <Box key={col.accessorKey || col.header} sx={{ mb: 0.5 }}>
                  <Typography variant="body2">
                    <strong>{col.header}</strong>
                  </Typography>

                  <Typography variant="body2">
                    {typeof col.Cell === "function"
                      ? col.Cell({
                          cell: { getValue: () => row[col.accessorKey] },
                          row: { original: row },
                          column: col,
                        })
                      : String(row[col.accessorKey] ?? "-")}
                  </Typography>
                </Box>
              ))}
            </ListItem>
          ))}
          {data.length === 0 && (
            <Typography sx={{ mt: 2 }}>Keine Einträge gefunden.</Typography>
          )}
        </List>
      )}

      {/* Paginierung */}
      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => {
              setPage(value);
              onChangePage?.({ pageIndex: value - 1, pageSize });
            }}
            color="primary"
            defaultPage={6}
            siblingCount={0}
          />
        </Box>
      )}
    </Box>
  );
}

DataList.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  hasLoaded: PropTypes.bool,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  totalRows: PropTypes.number,
  onChangePage: PropTypes.func,
  // onChangeSorting: PropTypes.func,
  onChangeFilters: PropTypes.func,
  onChangeGlobalFilter: PropTypes.func,
  toolbarActions: PropTypes.array,
  filterTabs: PropTypes.array,
  activeFilterTab: PropTypes.string,
  filterOptions: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // storageKey: PropTypes.string,
};

import { Skeleton, ListItemAvatar, ListItemText } from "@mui/material";

function SkeletonList() {
  return (
    <List>
      {[1, 2, 3, 4].map((item) => (
        <ListItem key={item}>
          <ListItemAvatar>
            <Skeleton variant="circular" width={40} height={40} />
          </ListItemAvatar>
          <ListItemText
            primary={<Skeleton variant="text" width="80%" />}
            secondary={<Skeleton variant="text" width="60%" />}
          />
        </ListItem>
      ))}
    </List>
  );
}
